import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { Play, Square, Plus, Trash2, Clock, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from '../hooks/use-toast';

const API = `${API_BASE_URL}/api`;

const fmt = (seconds) => {
  if (!seconds) return '0m';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const fmtLive = (seconds) => {
  const h = Math.floor(seconds / 3600).toString().padStart(2, '0');
  const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${h}:${m}:${s}`;
};

// ── Inline timer button shown in task rows ──────────────────────────────────
export const TimerButton = ({ taskId, onUpdate }) => {
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [loading, setLoading] = useState(false);
  const intervalRef = useRef(null);
  const startRef = useRef(null);

  // Check running state on mount
  useEffect(() => {
    axios.get(`${API}/tasks/${taskId}/time`, { withCredentials: true })
      .then(r => {
        if (r.data.running_since) {
          const since = new Date(r.data.running_since);
          const secs = Math.floor((Date.now() - since.getTime()) / 1000);
          setElapsed(secs);
          startRef.current = since;
          setRunning(true);
        }
      })
      .catch(() => {});
  }, [taskId]);

  // Tick when running
  useEffect(() => {
    if (running) {
      intervalRef.current = setInterval(() => {
        setElapsed(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [running]);

  const handleToggle = async (e) => {
    e.stopPropagation();
    setLoading(true);
    try {
      if (running) {
        await axios.post(`${API}/tasks/${taskId}/time/stop`, {}, { withCredentials: true });
        setRunning(false);
        setElapsed(0);
        toast({ title: `Temps enregistré : ${fmtLive(elapsed)}` });
        onUpdate?.();
      } else {
        await axios.post(`${API}/tasks/${taskId}/time/start`, {}, { withCredentials: true });
        setRunning(true);
        setElapsed(0);
        startRef.current = new Date();
      }
    } catch (err) {
      toast({ title: 'Erreur timer', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleToggle}
      disabled={loading}
      className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
        running
          ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100'
          : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
      }`}
      title={running ? 'Arrêter le timer' : 'Démarrer le timer'}
    >
      {loading ? (
        <Loader2 className="w-3 h-3 animate-spin" />
      ) : running ? (
        <>
          <Square className="w-3 h-3 fill-current" />
          <span className="font-mono">{fmtLive(elapsed)}</span>
        </>
      ) : (
        <Play className="w-3 h-3" />
      )}
    </button>
  );
};

// ── Full time panel shown in the board Sheet ────────────────────────────────
const TimeTracker = ({ boardId, tasks }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [manualTask, setManualTask] = useState('');
  const [manualMinutes, setManualMinutes] = useState('');
  const [manualNote, setManualNote] = useState('');
  const [adding, setAdding] = useState(false);

  const fetchSummary = async () => {
    try {
      const r = await axios.get(`${API}/boards/${boardId}/time`, { withCredentials: true });
      setSummary(r.data);
    } catch {
      toast({ title: 'Erreur chargement', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (boardId) fetchSummary();
  }, [boardId]);

  const handleAddManual = async () => {
    if (!manualTask || !manualMinutes) return;
    setAdding(true);
    try {
      await axios.post(
        `${API}/tasks/${manualTask}/time`,
        { duration_minutes: parseInt(manualMinutes, 10), note: manualNote },
        { withCredentials: true }
      );
      toast({ title: 'Entrée ajoutée' });
      setManualTask(''); setManualMinutes(''); setManualNote('');
      fetchSummary();
    } catch (err) {
      toast({ title: err.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally {
      setAdding(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-40">
      <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
    </div>
  );

  const byTask = summary?.by_task || {};
  const byUser = summary?.by_user || {};
  const totalSeconds = summary?.total_seconds || 0;

  // Map task_id → task name
  const taskNameMap = {};
  (tasks || []).forEach(t => { taskNameMap[t._id] = t.task; });

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-4 border-b dark:border-gray-700">
        <div className="flex items-center gap-2 mb-1">
          <Clock className="w-4 h-4 text-[#1C3E53] dark:text-[#4A90A4]" />
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Time Tracking</h2>
        </div>
        <p className="text-2xl font-bold text-[#1C3E53] dark:text-[#4A90A4]">{fmt(totalSeconds)}</p>
        <p className="text-xs text-gray-400 dark:text-gray-500">Total sur ce board</p>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
        {/* Par tâche */}
        {Object.keys(byTask).length > 0 && (
          <div>
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Par tâche</p>
            <div className="space-y-2">
              {Object.entries(byTask)
                .sort((a, b) => b[1] - a[1])
                .map(([tid, secs]) => (
                  <div key={tid} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <span className="text-sm text-gray-800 dark:text-gray-200 truncate flex-1 mr-2">
                      {taskNameMap[tid] || tid}
                    </span>
                    <span className="text-sm font-semibold text-[#1C3E53] dark:text-[#4A90A4] flex-shrink-0">
                      {fmt(secs)}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Par membre */}
        {Object.keys(byUser).length > 0 && (
          <div>
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Par membre</p>
            <div className="space-y-2">
              {Object.entries(byUser)
                .sort((a, b) => b[1] - a[1])
                .map(([name, secs]) => {
                  const pct = totalSeconds > 0 ? (secs / totalSeconds) * 100 : 0;
                  return (
                    <div key={name}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-gray-700 dark:text-gray-300">{name}</span>
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{fmt(secs)}</span>
                      </div>
                      <div className="h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                        <div className="h-full bg-[#1C3E53] dark:bg-[#4A90A4] rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* Ajout manuel */}
        <div>
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            Ajouter une entrée manuelle
          </p>
          <div className="space-y-2">
            <select
              value={manualTask}
              onChange={e => setManualTask(e.target.value)}
              className="w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#1C3E53]/30"
            >
              <option value="">Sélectionner une tâche...</option>
              {(tasks || []).map(t => (
                <option key={t._id} value={t._id}>{t.task}</option>
              ))}
            </select>
            <div className="flex gap-2">
              <Input
                type="number"
                placeholder="Durée (min)"
                value={manualMinutes}
                onChange={e => setManualMinutes(e.target.value)}
                min="1"
                className="w-32 text-sm"
              />
              <Input
                placeholder="Note (optionnel)"
                value={manualNote}
                onChange={e => setManualNote(e.target.value)}
                className="flex-1 text-sm"
              />
            </div>
            <Button
              onClick={handleAddManual}
              disabled={!manualTask || !manualMinutes || adding}
              size="sm"
              className="w-full bg-[#1C3E53] hover:bg-[#2A5470] dark:bg-[#4A90A4] dark:hover:bg-[#5AA0B4] text-white"
            >
              {adding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Ajouter
            </Button>
          </div>
        </div>

        {totalSeconds === 0 && (
          <div className="text-center py-8 text-gray-400 dark:text-gray-500">
            <Clock className="w-10 h-10 mx-auto mb-3" />
            <p className="text-sm">Aucun temps enregistré sur ce board.</p>
            <p className="text-xs mt-1">Utilisez le bouton ▶ sur les tâches pour démarrer un chrono.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeTracker;
